// Вкладка «История» админ-консоли: этапы, категория ИИ-помощника, промпт.
// Выделено из admin.ts (аудит, этап 2 — декомпозиция).
import { state } from '../shared/state';
import { $, showToast } from '../shared/index';
import { type ApiErrorBody, formatApiErrors, getAdminCategories } from './admin-shared';

interface JourneyStage {
  name: string;
  description: string;
}

let adminJourneyStages: JourneyStage[] = [];

export async function loadAdminJourneySettings() {
  try {
    const cats = await getAdminCategories();
    const select = $<HTMLSelectElement>('admin-journey-help-category');
    if (select) {
      select.textContent = '';

      const emptyOpt = document.createElement('option');
      emptyOpt.value = '';
      emptyOpt.textContent = '— Без ИИ-помощника —';
      select.appendChild(emptyOpt);

      Object.keys(cats || {}).sort().forEach(cname => {
        const opt = document.createElement('option');
        opt.value = cname;
        opt.textContent = cname;
        select.appendChild(opt);
      });
    }

    const r = await fetch('/api/admin/journey/settings', {
      headers: { 'Authorization': 'Bearer ' + state.authToken }
    });
    if (!r.ok) {
      throw new Error('Не удалось загрузить настройки историй');
    }
    const data = await r.json() as { stages: JourneyStage[]; help_category: string; assistant_prompt_template?: string };

    adminJourneyStages = data.stages || [];
    if (select) {
      select.value = data.help_category || '';
    }

    const promptEl = $<HTMLTextAreaElement>('admin-journey-assistant-prompt');
    if (promptEl) {
      promptEl.value = data.assistant_prompt_template || '';
    }

    renderAdminJourneyStages();
  } catch (e) {
    console.error(e);
    showToast('❌ ' + (e instanceof Error ? e.message : String(e)));
  }
}

function renderAdminJourneyStages() {
  const container = $('admin-journey-stages-container');
  if (!container) return;
  container.textContent = '';

  if (adminJourneyStages.length === 0) {
    container.innerHTML = '<div class="audit-log-empty">Нет настроенных этапов. Добавьте первый этап.</div>';
    return;
  }

  adminJourneyStages.forEach((stage, idx) => {
    const el = document.createElement('div');
    el.className = 'user-item journey-stage-row';
    el.style.display = 'flex';
    el.style.flexDirection = 'column';
    el.style.gap = '8px';
    el.style.marginBottom = '12px';
    el.style.padding = '12px';
    el.style.border = '1px solid var(--border-color, #eee)';
    el.style.borderRadius = '6px';

    el.innerHTML = `
      <div style="display: flex; align-items: center; gap: 8px; width: 100%;">
        <span class="stage-number" style="font-weight: bold; min-width: 24px; color: var(--color-accent, #007acc);">${idx + 1}</span>
        <input type="text" class="setup-input admin-stage-name" style="flex: 1;" value="${DOMPurify.sanitize(stage.name)}" placeholder="Название этапа">
        <button type="button" class="nav-btn btn-action-sm move-up-stage" data-idx="${idx}">▲</button>
        <button type="button" class="nav-btn btn-action-sm move-down-stage" data-idx="${idx}">▼</button>
        <button type="button" class="nav-btn btn-action-sm--danger delete-stage" data-idx="${idx}">🗑️</button>
      </div>
      <div style="width: 100%;">
        <textarea class="setup-input admin-stage-desc" rows="2" style="width: 100%; resize: vertical;" placeholder="Пояснение (2-3 предложения)">${DOMPurify.sanitize(stage.description)}</textarea>
      </div>
    `;

    el.querySelector('.admin-stage-name')?.addEventListener('input', (e) => {
      adminJourneyStages[idx].name = (e.target as HTMLInputElement).value;
    });

    el.querySelector('.admin-stage-desc')?.addEventListener('input', (e) => {
      adminJourneyStages[idx].description = (e.target as HTMLTextAreaElement).value;
    });

    el.querySelector('.move-up-stage')?.addEventListener('click', () => {
      if (idx > 0) {
        const tmp = adminJourneyStages[idx];
        adminJourneyStages[idx] = adminJourneyStages[idx - 1];
        adminJourneyStages[idx - 1] = tmp;
        renderAdminJourneyStages();
      }
    });

    el.querySelector('.move-down-stage')?.addEventListener('click', () => {
      if (idx < adminJourneyStages.length - 1) {
        const tmp = adminJourneyStages[idx];
        adminJourneyStages[idx] = adminJourneyStages[idx + 1];
        adminJourneyStages[idx + 1] = tmp;
        renderAdminJourneyStages();
      }
    });

    el.querySelector('.delete-stage')?.addEventListener('click', () => {
      adminJourneyStages.splice(idx, 1);
      renderAdminJourneyStages();
    });

    container.appendChild(el);
  });
}

export function bindAdminJourneyEvents() {
  $('btn-add-journey-stage')?.addEventListener('click', () => {
    adminJourneyStages.push({ name: '', description: '' });
    renderAdminJourneyStages();
  });

  $('btn-save-journey-settings')?.addEventListener('click', async () => {
    const helpCategory = $<HTMLSelectElement>('admin-journey-help-category')?.value || '';
    const assistantPrompt = $<HTMLTextAreaElement>('admin-journey-assistant-prompt')?.value || '';

    for (let i = 0; i < adminJourneyStages.length; i++) {
      const stage = adminJourneyStages[i];
      if (!stage.name.trim()) {
        showToast(`❌ Название этапа ${i + 1} не может быть пустым`);
        return;
      }
    }

    try {
      const r = await fetch('/api/admin/journey/settings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + state.authToken
        },
        body: JSON.stringify({
          stages: adminJourneyStages,
          help_category: helpCategory,
          assistant_prompt_template: assistantPrompt
        })
      });

      if (r.ok) {
        showToast('✅ Настройки историй сохранены');
        loadAdminJourneySettings();
      } else {
        const err = await r.json() as ApiErrorBody;
        showToast('❌ Ошибка сохранения: ' + formatApiErrors(err));
      }
    } catch (e) {
      console.error(e);
      showToast('❌ Ошибка сети при сохранении');
    }
  });
}
